import {Button, Icon, icon, TextRegular, useSortableTable} from '@hm/fundamental'
import React from 'react'

import {TextWithCaption} from '../../../../common/components/TextWithCaption/TextWithCaption'
import {
  EquipmentOption,
  LimsSamplingPoint,
  SamplingPointTypeOption,
  SortByColumn,
  SortDirection,
  UpmDepartment
} from '../../../types'
import {SamplingPointTypeEquipmentForm} from '../SamplingPointTypeEquipmentForm'

interface UseSamplingPointsSettingsTableConfigParameters {
  isMobile: boolean
  sortBy: SortByColumn
  sortDirection: SortDirection
  onToggleSorting: (column: SortByColumn) => void
  getSamplingPointsCount: (limsSamplingPoint: LimsSamplingPoint) => number
  t: (translationKey: string) => string
  samplingPointTypeOptions: SamplingPointTypeOption[]
  assetOptions: EquipmentOption[]
  grindingCircuitOptions: EquipmentOption[]
  storageOptions: EquipmentOption[]
  upmDepartmentOptions: UpmDepartment[]
  isSaving: boolean
  pinnedSamplingPointIds: Set<number>
  onTogglePinnedSamplingPoint: (limsSamplingPointId: number) => void
  lessUsedSamplingPointIds: Set<number>
  onToggleLessUsedSamplingPoint: (limsSamplingPointId: number) => void
}

interface SortButtonProps {
  label: string
  column: SortByColumn
  sortBy: SortByColumn
  sortDirection: SortDirection
  onToggleSorting: (column: SortByColumn) => void
  buttonTestId: string
  indicatorTestId: string
  textClassName: string
  align?: 'left' | 'center'
}

const SortButton: React.FC<SortButtonProps> = ({
  label,
  column,
  sortBy,
  sortDirection,
  onToggleSorting,
  buttonTestId,
  indicatorTestId,
  textClassName,
  align = 'left'
}) => (
  <Button
    variant="tertiary"
    colorClass="text-gray-700 hover:text-gray-900"
    data-test-id={buttonTestId}
    onClick={(event) => {
      event.preventDefault()
      event.stopPropagation()
      onToggleSorting(column)
    }}
    className={`grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-1 whitespace-normal break-words border-0 bg-transparent p-0 m-0 normal-case cursor-pointer ${align === 'center' ? 'text-center leading-tight' : 'text-left'}`}
  >
    <TextRegular
      className={`${textClassName} min-w-0 whitespace-normal break-words ${align === 'center' ? 'text-center leading-tight' : ''}`}
    >
      {label}
    </TextRegular>
    <TextRegular className="shrink-0 whitespace-nowrap text-right" data-test-id={indicatorTestId}>
      {sortBy === column ? (sortDirection === SortDirection.ASCENDING ? '↑' : '↓') : ''}
    </TextRegular>
  </Button>
)

const headerTextClassName =
  'mb-0 min-w-0 whitespace-normal break-words text-base font-medium leading-6 text-gray-700'
const headerActionButtonsClassName =
  'inline-flex min-w-0 items-center justify-center rounded border border-gray-300 bg-white p-1 text-gray-700 transition-colors hover:border-gray-400 hover:text-gray-900'
const headerInfoIconClassName =
  'inline-flex h-2 w-2 shrink-0 items-center justify-center rounded-full border border-gray-500 text-[8px] font-semibold leading-none text-gray-700'

const HeaderLabelWithInfo = ({
  label,
  tooltip,
  testId
}: {
  label: string
  tooltip: string
  testId: string
}) => (
  <TextRegular className={headerTextClassName}>
    <span className="inline-flex w-full min-w-0 items-center justify-center gap-0.5">
      <span className="text-center">{label}</span>
      <span
        className="group relative inline-flex cursor-help shrink-0"
        data-test-id={testId}
        aria-label={tooltip}
      >
        <span className={headerInfoIconClassName} aria-hidden="true">
          i
        </span>
        <span
          role="tooltip"
          data-test-id={`${testId}_tooltip`}
          className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white opacity-0 transition-opacity group-hover:opacity-100"
        >
          {tooltip}
        </span>
      </span>
    </span>
  </TextRegular>
)

export const useSamplingPointsSettingsTableConfig = ({
  isMobile,
  sortBy,
  sortDirection,
  onToggleSorting,
  getSamplingPointsCount,
  t,
  samplingPointTypeOptions,
  assetOptions,
  grindingCircuitOptions,
  storageOptions,
  upmDepartmentOptions,
  isSaving,
  pinnedSamplingPointIds,
  onTogglePinnedSamplingPoint,
  lessUsedSamplingPointIds,
  onToggleLessUsedSamplingPoint
}: UseSamplingPointsSettingsTableConfigParameters) => {
  const getTableLabel = React.useCallback(
    (label: string) =>
      isMobile ? '' : <TextRegular className={headerTextClassName}>{label}</TextRegular>,
    [isMobile]
  )

  const pinnedColumnLabel = React.useMemo(
    () =>
      isMobile ? (
        ''
      ) : (
        <HeaderLabelWithInfo
          label={t('samplingPointsSettings.pin')}
          tooltip={t('samplingPointsSettings.pinHeaderInfoTooltip')}
          testId="sampling_points_table_pin_info_icon"
        />
      ),
    [isMobile, t]
  )

  const lessUsedColumnLabel = React.useMemo(
    () =>
      isMobile ? (
        ''
      ) : (
        <HeaderLabelWithInfo
          label={t('samplingPointsSettings.lessUsedRows')}
          tooltip={t('samplingPointsSettings.lessUsedHeaderInfoTooltip')}
          testId="sampling_points_table_less_used_info_icon"
        />
      ),
    [isMobile, t]
  )

  const codeColumnLabel = React.useMemo(
    () =>
      isMobile ? (
        ''
      ) : (
        <SortButton
          label={t('samplingPointsSettings.samplingPointCode')}
          column={SortByColumn.CODE}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onToggleSorting={onToggleSorting}
          buttonTestId="sampling_points_table_code_sort_toggle_desktop"
          indicatorTestId="sampling_points_table_code_sort_indicator"
          textClassName={headerTextClassName}
          align="left"
        />
      ),
    [isMobile, onToggleSorting, t, sortBy, sortDirection]
  )

  const countsColumnLabel = React.useMemo(
    () =>
      isMobile ? (
        ''
      ) : (
        <SortButton
          label={t('samplingPointsSettings.samplingPointCounts')}
          column={SortByColumn.COUNTS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onToggleSorting={onToggleSorting}
          buttonTestId="sampling_points_table_counts_sort_toggle_desktop"
          indicatorTestId="sampling_points_table_counts_sort_indicator"
          textClassName={headerTextClassName}
          align="center"
        />
      ),
    [isMobile, onToggleSorting, t, sortBy, sortDirection]
  )

  const tableConfig = useSortableTable<LimsSamplingPoint>(
    [
      {
        id: 'description',
        label: pinnedColumnLabel,
        headerClassName:
          'w-16 min-w-0 h-auto whitespace-normal break-words text-center align-middle',
        valueClassName: 'w-16 min-w-0 text-center',
        render: (limsSamplingPoint) => {
          const isPinned = pinnedSamplingPointIds.has(limsSamplingPoint.id)

          return (
            <Button
              variant="secondary"
              aria-label={
                isPinned
                  ? t('samplingPointsSettings.unpinFavourite')
                  : t('samplingPointsSettings.pinFavourite')
              }
              aria-pressed={isPinned}
              data-test-id={`sampling_points_table_pin_toggle_${limsSamplingPoint.id}`}
              onClick={() => {
                if (isSaving) {
                  return
                }
                onTogglePinnedSamplingPoint(limsSamplingPoint.id)
              }}
              disabled={isSaving}
              size="small"
              square
              className={headerActionButtonsClassName}
            >
              <Icon path={isPinned ? icon.mdiPin : icon.mdiPinOutline} size="regular" />
            </Button>
          )
        }
      },
      {
        id: 'samplingPoint',
        label: lessUsedColumnLabel,
        headerClassName:
          'w-16 min-w-0 h-auto whitespace-normal break-words text-center align-middle',
        valueClassName: 'w-16 min-w-0 text-center pr-1',
        render: (limsSamplingPoint) => {
          const isLessUsed = lessUsedSamplingPointIds.has(limsSamplingPoint.id)

          return (
            <Button
              variant="secondary"
              aria-label={
                isLessUsed
                  ? t('samplingPointsSettings.removeFromLessUsed')
                  : t('samplingPointsSettings.addToLessUsed')
              }
              aria-pressed={isLessUsed}
              data-test-id={`sampling_points_table_less_used_toggle_${limsSamplingPoint.id}`}
              onClick={() => {
                if (isSaving) {
                  return
                }
                onToggleLessUsedSamplingPoint(limsSamplingPoint.id)
              }}
              disabled={isSaving}
              size="small"
              square
              className={headerActionButtonsClassName}
            >
              <Icon
                path={isLessUsed ? icon.mdiArchiveArrowUp : icon.mdiArchiveArrowDown}
                size="regular"
              />
            </Button>
          )
        }
      },
      {
        id: 'code',
        label: codeColumnLabel,
        headerClassName: 'min-w-0 h-auto whitespace-normal break-words align-middle',
        valueClassName: 'min-w-0 pl-1',
        render: (limsSamplingPoint) => (
          <div className="w-full min-w-0 break-words">
            <TextWithCaption
              text={limsSamplingPoint.code}
              caption={limsSamplingPoint.description}
              data-test-id="sampling_point_code_value"
            />
          </div>
        )
      },
      {
        id: 'id',
        label: countsColumnLabel,
        headerClassName: 'min-w-0 h-auto whitespace-normal break-words align-middle',
        valueClassName: 'min-w-0 whitespace-normal break-words text-left',
        render: (limsSamplingPoint) => (
          <TextWithCaption
            text={String(getSamplingPointsCount(limsSamplingPoint))}
            data-test-id="sampling_point_count_value"
          />
        )
      },
      {
        label: getTableLabel(t('samplingPointsSettings.samplingPointType')),
        headerClassName: 'min-w-0 h-auto whitespace-normal break-words align-middle',
        valueClassName: 'min-w-0',
        render: (limsSamplingPoint) => (
          <SamplingPointTypeEquipmentForm
            rowId={limsSamplingPoint.id}
            samplingPointTypeOptions={samplingPointTypeOptions}
            assetOptions={assetOptions}
            grindingCircuitOptions={grindingCircuitOptions}
            storageOptions={storageOptions}
            upmDepartmentOptions={upmDepartmentOptions}
            isSaving={isSaving}
            field="type"
          />
        )
      },
      {
        label: getTableLabel(t('samplingPointsSettings.samplingPointEquipment')),
        headerClassName: 'min-w-0 h-auto whitespace-normal break-words align-middle',
        valueClassName: 'min-w-0',
        render: (limsSamplingPoint) => (
          <SamplingPointTypeEquipmentForm
            rowId={limsSamplingPoint.id}
            samplingPointTypeOptions={samplingPointTypeOptions}
            assetOptions={assetOptions}
            grindingCircuitOptions={grindingCircuitOptions}
            storageOptions={storageOptions}
            upmDepartmentOptions={upmDepartmentOptions}
            isSaving={isSaving}
            field="equipment"
          />
        )
      },
      {
        label: getTableLabel(t('samplingPointsSettings.samplingPointUpmDepartment')),
        headerClassName: 'min-w-0 h-auto whitespace-normal break-words align-middle',
        valueClassName: 'min-w-0',
        render: (limsSamplingPoint) => (
          <SamplingPointTypeEquipmentForm
            rowId={limsSamplingPoint.id}
            samplingPointTypeOptions={samplingPointTypeOptions}
            assetOptions={assetOptions}
            grindingCircuitOptions={grindingCircuitOptions}
            storageOptions={storageOptions}
            upmDepartmentOptions={upmDepartmentOptions}
            isSaving={isSaving}
            field="upmDepartment"
          />
        )
      }
    ],
    [
      isMobile,
      sortBy,
      sortDirection,
      onToggleSorting,
      getSamplingPointsCount,
      t,
      samplingPointTypeOptions,
      assetOptions,
      grindingCircuitOptions,
      storageOptions,
      upmDepartmentOptions,
      isSaving,
      pinnedSamplingPointIds,
      onTogglePinnedSamplingPoint,
      lessUsedSamplingPointIds,
      onToggleLessUsedSamplingPoint
    ],
    {
      prefix: 'sampling-point-row',
      postfixFieldName: 'id'
    },
    {enableLocalSorting: false}
  )
  return tableConfig
}
